import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import { Package, Truck, RotateCcw, IndianRupee, LayoutGrid, MapPin, Boxes, AlertTriangle, X, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '@/api/client';
import { moneyCompact } from '@/utils/money';
import { useAuth } from '@/hooks/useAuth';
import { KpiValueSkeleton, TableSkeleton, Skeleton, ChartSkeleton } from '@/components/ui/Skeleton';

// ── Drill-down: descriptor for the modal that lists the orders behind a card / table row ──
type Drill = {
  kind: 'card' | 'status' | 'zone' | 'courier' | 'ndr';
  label: string;
  metric?: string; statusId?: number; zoneCode?: string; courierId?: number; ndrCode?: number;
  deliveryType?: 'COD' | 'PREPAID';
};
const dtFromMetric = (m: Metric): 'COD' | 'PREPAID' | undefined =>
  m === 'cod' ? 'COD' : m === 'prepaid' ? 'PREPAID' : undefined;

type Split = { total: number; cod: number; prepaid: number };
type StatusRow = Split & { statusId: number; label: string };
type ZoneRow = Split & { zoneCode: string; label: string };
type CourierRow = { courierId: number; name: string; assigned: number; cod: number; prepaid: number; statuses: StatusRow[] };
type NdrByCourier = Record<string, { total: number; actionTaken: number; notTaken: number }>;
type NdrReason = { ndrCode: number; label: string; total: number; actionTaken: number; notTaken: number; byCourier: NdrByCourier };
type Dash = {
  range: { from: string; to: string };
  cards: {
    totalShipment: number; todayShipment: number;
    totalDelivered: number; todayDelivered: number;
    totalRTO: number; todayRTO: number; bookedDelivered: number; deliveryRate: number;
    totalRevenue: number; todayRevenue: number;
  };
  performance: {
    base: number;
    delivered: { count: number; pct: number };
    rto: { count: number; pct: number };
    pending: { count: number; pct: number };
    deliveryFailed: { count: number; pct: number };
    lost: { count: number; pct: number };
    excluded: number;
  };
  statusWise: StatusRow[];
  zoneWise: ZoneRow[];
  couriers: CourierRow[];
  ndr: { couriers: { courierId: number; name: string }[]; reasons: NdrReason[] };
};

type Metric = 'total' | 'cod' | 'prepaid';
const PALETTE = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899', '#6366f1', '#84cc16', '#f97316'];
const money = (n: number) => Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const num = (n: number) => Number(n ?? 0).toLocaleString('en-IN');

function daysAgo(n: number) {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
}
const pick = (r: { cod: number; prepaid: number; total?: number; assigned?: number }, m: Metric) =>
  (m === 'cod' ? r.cod : m === 'prepaid' ? r.prepaid : (r.total ?? r.assigned ?? (r.cod + r.prepaid)));

export function MerchantDashboardPage() {
  const { user } = useAuth();
  const serviceType = (user as any)?.serviceType ?? 'BOTH';
  const showB2c = serviceType !== 'B2B';
  const showB2b = serviceType !== 'B2C';
  const tabs = ([showB2c ? 'B2C' : null, showB2b ? 'B2B' : null].filter(Boolean) as ('B2C' | 'B2B')[]);
  const [tab, setTab] = useState<'B2C' | 'B2B'>(tabs[0] ?? 'B2C');

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><LayoutGrid size={22} className="text-emerald-700" /> Dashboard</h1>
      </div>
      {tabs.length > 1 && (
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
          {tabs.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-6 py-1.5 text-sm rounded-md transition ${tab === t ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
              {t}
            </button>
          ))}
        </div>
      )}
      <DashboardBody key={tab} orderType={tab} />
    </div>
  );
}

function DashboardBody({ orderType }: { orderType: 'B2C' | 'B2B' }) {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [sub, setSub] = useState<'shipment' | 'zone' | 'courier' | 'ndr'>('shipment');
  const [drill, setDrill] = useState<Drill | null>(null);

  const { data, isLoading } = useQuery<Dash>({
    queryKey: ['merchant-dashboard', orderType, from, to],
    queryFn: async () => (await api.get<Dash>('/dashboard/merchant', { params: { orderType, from, to } })).data,
  });

  const c = data?.cards;
  return (
    <div>
      {/* Date range */}
      <div className="flex items-end gap-2 mb-4">
        <div>
          <label className="block text-xs text-slate-500 mb-0.5">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-0.5">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div className="flex gap-1 ml-2">
          {[
            { l: 'Last 30 days', from: 29, to: 0 },
            { l: 'Last 7 days', from: 6, to: 0 },
            { l: 'Yesterday', from: 1, to: 1 },
            { l: 'Today', from: 0, to: 0 },
          ].map((p) => {
            const active = from === daysAgo(p.from) && to === daysAgo(p.to);
            return (
              <button key={p.l} onClick={() => { setFrom(daysAgo(p.from)); setTo(daysAgo(p.to)); }}
                className={`text-xs px-2 py-1.5 border rounded transition ${active ? 'bg-primary text-white border-primary' : 'text-slate-600 hover:bg-slate-50'}`}>{p.l}</button>
            );
          })}
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Card icon={<Package size={18} />} tone="blue" label="Total Shipment" value={num(c?.totalShipment ?? 0)} sub={`${num(c?.todayShipment ?? 0)} today`} loading={isLoading}
          onClick={() => setDrill({ kind: 'card', metric: 'shipment', label: 'Total shipments' })} />
        {/* Counts deliveries that HAPPENED in the selected period (by delivery date), so this agrees
            with the operations dashboard for the same date range instead of diverging from it. */}
        <Card icon={<Truck size={18} />} tone="emerald" label="Delivered in period" value={num(c?.totalDelivered ?? 0)}
          sub={`${num(c?.bookedDelivered ?? 0)} of ${num(c?.totalShipment ?? 0)} booked (${c?.deliveryRate ?? 0}%)`} loading={isLoading}
          onClick={() => setDrill({ kind: 'card', metric: 'delivered', label: 'Shipments delivered in this period' })} />
        {/* RTO carried a raw count and a "today" figure but no share, so there was no way to tell
            whether 40 returns was a good month or a bad one. Measured against the shipments that
            actually shipped (the same base the Delivery Performance strip uses), so the two agree. */}
        <Card icon={<RotateCcw size={18} />} tone="rose" label="RTO delivered in period" value={num(c?.totalRTO ?? 0)}
          sub={`${num(c?.todayRTO ?? 0)} today · ${data?.performance ? `${data.performance.rto.pct}% RTO rate` : '—'}`} loading={isLoading}
          onClick={() => setDrill({ kind: 'card', metric: 'rto', label: 'Shipments RTO-delivered in this period' })} />
        {/* Compacted: past a lakh the full figure overflows the tile and stops being readable at a
            glance. Exact value stays on hover — see utils/money.ts. */}
        <Card icon={<IndianRupee size={18} />} tone="violet" label="Total Revenue"
          value={moneyCompact(c?.totalRevenue ?? 0)} exact={`₹${money(c?.totalRevenue ?? 0)}`}
          sub={`${moneyCompact(c?.todayRevenue ?? 0)} today`} loading={isLoading}
          onClick={() => setDrill({ kind: 'card', metric: 'revenue', label: 'Shipments (revenue)' })} />
      </div>

      {/* Delivery performance — the four outcomes as a share of everything that actually shipped */}
      <PerformanceStrip perf={data?.performance} loading={isLoading} onDrill={setDrill} />

      {/* Sub-tabs */}
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 mb-4">
        {([
          { id: 'shipment', label: 'Shipment Overview', icon: <Package size={14} /> },
          { id: 'zone', label: 'Zone Overview', icon: <MapPin size={14} /> },
          { id: 'courier', label: 'Courier Performance', icon: <Boxes size={14} /> },
          { id: 'ndr', label: 'NDR Overview', icon: <AlertTriangle size={14} /> },
        ] as const).map((s) => (
          <button key={s.id} onClick={() => setSub(s.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 transition ${sub === s.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-5"><ChartSkeleton /></div>
      ) : sub === 'shipment' ? (
        <ShipmentOverview rows={data?.statusWise ?? []} onDrill={setDrill} />
      ) : sub === 'zone' ? (
        <ZoneOverview rows={data?.zoneWise ?? []} onDrill={setDrill} />
      ) : sub === 'courier' ? (
        <CourierOverview couriers={data?.couriers ?? []} onDrill={setDrill} />
      ) : (
        <NdrOverview ndr={data?.ndr ?? { couriers: [], reasons: [] }} onDrill={setDrill} />
      )}

      <DrillModal drill={drill} onClose={() => setDrill(null)} orderType={orderType} from={from} to={to} />
    </div>
  );
}

/**
 * Delivered / RTO / Pending / Delivery Failed as a percentage of everything that actually shipped.
 *
 * The denominator EXCLUDES cancelled, not-shipped and booking-failed orders, and the strip says so
 * out loud: a merchant reading "62% delivered" without that caveat would be looking at their own
 * upload backlog rather than their courier's performance, and would take it to the courier.
 *
 * The four bars are mutually exclusive and total 100%, so the stacked bar is a real proportion.
 */
function PerformanceStrip({ perf, loading, onDrill }: { perf?: Dash['performance']; loading?: boolean; onDrill: (d: Drill) => void }) {
  const segments = [
    { key: 'delivered', label: 'Delivered', metric: 'delivered', value: perf?.delivered, colour: 'bg-emerald-500', text: 'text-emerald-700', chip: 'bg-emerald-50' },
    { key: 'rto', label: 'RTO', metric: 'rto', value: perf?.rto, colour: 'bg-rose-500', text: 'text-rose-700', chip: 'bg-rose-50' },
    { key: 'pending', label: 'Pending', metric: 'shipment', value: perf?.pending, colour: 'bg-amber-500', text: 'text-amber-700', chip: 'bg-amber-50' },
    { key: 'failed', label: 'Delivery Failed', metric: 'shipment', value: perf?.deliveryFailed, colour: 'bg-slate-500', text: 'text-slate-700', chip: 'bg-slate-100' },
  ] as const;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Delivery Performance</h3>
        <span className="text-xs text-slate-400">
          {loading ? 'Loading…' : `of ${num(perf?.base ?? 0)} shipped`}
          {!loading && !!perf?.excluded && (
            <span title="Cancelled, not shipped and booking-failed orders have no delivery outcome, so they are left out of these percentages">
              {' '}· {num(perf.excluded)} excluded
            </span>
          )}
        </span>
      </div>

      <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100 mb-3">
        {segments.map(s => (
          <div key={s.key} className={s.colour} style={{ width: `${s.value?.pct ?? 0}%` }} title={`${s.label} — ${s.value?.pct ?? 0}%`} />
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {segments.map(s => (
          <button key={s.key} onClick={() => onDrill({ kind: 'card', metric: s.metric, label: s.label })}
            className={`text-left rounded-lg p-3 ${s.chip} hover:ring-2 hover:ring-slate-200 transition`}>
            <div className="text-xs text-slate-500">{s.label}</div>
            <div className={`text-2xl font-bold ${s.text}`}>
              {loading ? <Skeleton className="h-7 w-16" /> : `${s.value?.pct ?? 0}%`}
            </div>
            <div className="text-[11px] text-slate-500">{num(s.value?.count ?? 0)} shipments</div>
          </button>
        ))}
      </div>

      {!loading && !!perf?.lost?.count && (
        <div className="text-[11px] text-slate-500 mt-2">
          Plus {num(perf.lost.count)} lost ({perf.lost.pct}%) — counted in the total but shown separately.
        </div>
      )}
    </div>
  );
}

function Card({ icon, label, value, exact, sub, tone, loading, onClick }: { icon: React.ReactNode; label: string; value: string; exact?: string; sub: string; tone: 'blue' | 'emerald' | 'rose' | 'violet'; loading?: boolean; onClick?: () => void }) {
  const tones: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700', emerald: 'bg-emerald-50 text-emerald-700', rose: 'bg-rose-50 text-rose-700', violet: 'bg-violet-50 text-violet-700',
  };
  return (
    <button type="button" onClick={onClick} disabled={!onClick || loading}
      className="text-left bg-white border border-slate-200 rounded-xl p-4 transition enabled:hover:border-emerald-300 enabled:hover:shadow-sm enabled:cursor-pointer group">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">{label}</div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[tone]}`}>{icon}</div>
      </div>
      {loading ? <KpiValueSkeleton /> : (
        <>
          <div className="text-2xl font-bold text-slate-900 mt-1" title={exact}>{value}</div>
          <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
            {sub}
            {onClick && <span className="text-emerald-600 opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-0.5">· view <ExternalLink size={10} /></span>}
          </div>
        </>
      )}
    </button>
  );
}

function MetricToggle({ value, onChange }: { value: Metric; onChange: (m: Metric) => void }) {
  return (
    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 text-xs">
      {(['total', 'cod', 'prepaid'] as Metric[]).map((m) => (
        <button key={m} onClick={() => onChange(m)}
          className={`px-3 py-1 rounded-md capitalize transition ${value === m ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-500 hover:text-slate-800'}`}>
          {m === 'total' ? 'All' : m === 'cod' ? 'COD' : 'Prepaid'}
        </button>
      ))}
    </div>
  );
}

function BarBlock({ data }: { data: { label: string; value: number }[] }) {
  if (!data.length) return <div className="h-[320px] flex items-center justify-center text-slate-400 text-sm">No data</div>;
  return (
    <ResponsiveContainer width="100%" height={Math.max(320, data.length * 26)}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v: any) => num(Number(v))} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
          {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * `n` as a share of `of`, to one decimal. Returns '—' rather than '0.0%' when there is nothing to
 * divide by: a dash reads as "no data", whereas 0.0% reads as a real measurement of zero.
 */
const share = (n: number, of: number) => (of > 0 ? `${((n / of) * 100).toFixed(1)}%` : '—');

type CountRow = { label: string; value: number; drill?: Drill };
function CountTable({ head, rows, onDrill }: { head: string; rows: CountRow[]; onDrill?: (d: Drill) => void }) {
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          {/* Share is of the TOTAL IN THIS TABLE, which is what the footer shows — so the column
              always adds to 100% and the reader can check it against the row beneath. */}
          <tr>
            <th className="text-left px-4 py-2.5">{head}</th>
            <th className="text-right px-4 py-2.5">Count</th>
            <th className="text-right px-4 py-2.5">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={3} className="text-center py-8 text-slate-400">No data</td></tr>
          ) : rows.map((r) => {
            const clickable = !!(onDrill && r.drill);
            const pct = total > 0 ? (r.value / total) * 100 : 0;
            return (
              <tr key={r.label}
                onClick={clickable ? () => onDrill!(r.drill!) : undefined}
                className={`border-t border-slate-100 ${clickable ? 'cursor-pointer hover:bg-emerald-50/60' : ''}`}>
                <td className="px-4 py-2 text-slate-700">
                  {clickable ? <span className="text-emerald-700 hover:underline inline-flex items-center gap-1">{r.label}<ExternalLink size={11} className="opacity-50" /></span> : r.label}
                </td>
                <td className="px-4 py-2 text-right font-medium tabular-nums">{num(r.value)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-500 whitespace-nowrap">
                  {/* A thin bar behind the number so the big rows are findable without reading
                      every percentage — the table is often 15+ statuses long. */}
                  <span className="inline-flex items-center gap-2 justify-end">
                    <span className="hidden sm:inline-block w-14 h-1.5 rounded-full bg-slate-100 overflow-hidden align-middle">
                      <span className="block h-full bg-emerald-400" style={{ width: `${Math.min(100, pct)}%` }} />
                    </span>
                    {share(r.value, total)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
        {rows.length > 0 && (
          <tfoot><tr className="border-t-2 bg-slate-50 font-semibold">
            <td className="px-4 py-2">Total</td>
            <td className="px-4 py-2 text-right tabular-nums">{num(total)}</td>
            <td className="px-4 py-2 text-right tabular-nums">100%</td>
          </tr></tfoot>
        )}
      </table>
    </div>
  );
}

function ShipmentOverview({ rows, onDrill }: { rows: StatusRow[]; onDrill: (d: Drill) => void }) {
  const [m, setM] = useState<Metric>('total');
  const view: CountRow[] = rows.map((r) => ({
    label: r.label, value: pick(r, m),
    drill: { kind: 'status' as const, statusId: r.statusId, deliveryType: dtFromMetric(m), label: r.label },
  })).filter((r) => r.value > 0);
  return (
    <Section title="Total Shipments — Status wise" metric={m} onMetric={setM}>
      <CountTable head="Status" rows={view} onDrill={onDrill} />
      <div className="bg-white border border-slate-200 rounded-xl p-3"><BarBlock data={view} /></div>
    </Section>
  );
}

function ZoneOverview({ rows, onDrill }: { rows: ZoneRow[]; onDrill: (d: Drill) => void }) {
  const [m, setM] = useState<Metric>('total');
  const view: CountRow[] = rows.map((r) => ({
    label: r.label, value: pick(r, m),
    drill: { kind: 'zone' as const, zoneCode: r.zoneCode, deliveryType: dtFromMetric(m), label: r.label },
  })).filter((r) => r.value > 0);
  return (
    <Section title="Zone-wise Shipments" metric={m} onMetric={setM}>
      <CountTable head="Zone" rows={view} onDrill={onDrill} />
      <div className="bg-white border border-slate-200 rounded-xl p-3"><BarBlock data={view} /></div>
    </Section>
  );
}

function CourierOverview({ couriers, onDrill }: { couriers: CourierRow[]; onDrill: (d: Drill) => void }) {
  const [m, setM] = useState<Metric>('total');
  const [sel, setSel] = useState<number | null>(couriers[0]?.courierId ?? null);
  const active = couriers.find((c) => c.courierId === sel) ?? couriers[0];
  const view: CountRow[] = (active?.statuses ?? []).map((s) => ({
    label: s.label, value: pick(s, m),
    drill: active ? { kind: 'courier' as const, courierId: active.courierId, statusId: s.statusId, deliveryType: dtFromMetric(m), label: `${active.name} · ${s.label}` } : undefined,
  })).filter((r) => r.value > 0);
  // Each courier's share of the shipments assigned across ALL couriers — "who carries what
  // proportion of my volume", which is the question the courier list is scanned for.
  const allAssigned = couriers.reduce((s, c) => s + pick(c, m), 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800">Courier Performance</h3>
        <MetricToggle value={m} onChange={setM} />
      </div>
      <div className="grid grid-cols-12 gap-4">
        {/* Courier list */}
        <div className="col-span-12 lg:col-span-3 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-3 py-2 text-xs uppercase text-slate-500 bg-slate-50 border-b">Couriers ({couriers.length})</div>
          <div className="max-h-[440px] overflow-y-auto">
            {couriers.length === 0 ? (
              <div className="p-4 text-sm text-slate-400 text-center">No couriers</div>
            ) : couriers.map((cr) => (
              <button key={cr.courierId} onClick={() => setSel(cr.courierId)}
                className={`w-full text-left px-3 py-2 border-b border-slate-50 flex items-center justify-between gap-2 ${active?.courierId === cr.courierId ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}>
                <span className="text-sm text-slate-700 truncate">{cr.name}</span>
                <span className="shrink-0 flex items-center gap-1.5">
                  <span className="text-[11px] text-slate-400 tabular-nums">{share(pick(cr, m), allAssigned)}</span>
                  <span className="text-xs font-semibold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{num(pick(cr, m))}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
        {/* Selected courier report */}
        <div className="col-span-12 lg:col-span-9 space-y-3">
          <div className="text-sm text-slate-600">{active ? <>Status-wise report for <b>{active.name}</b> · assigned <b>{num(pick(active, m))}</b> <span className="text-slate-400">({share(pick(active, m), allAssigned)} of all shipments)</span></> : 'Select a courier'}</div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <CountTable head="Status" rows={view} onDrill={onDrill} />
            <div className="bg-white border border-slate-200 rounded-xl p-3"><BarBlock data={view} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NdrOverview({ ndr, onDrill }: { ndr: Dash['ndr']; onDrill: (d: Drill) => void }) {
  const [courier, setCourier] = useState<string>(''); // '' = all couriers
  const rows = useMemo(() => {
    return ndr.reasons.map((r) => {
      if (!courier) return { label: r.label, ndrCode: r.ndrCode, total: r.total, actionTaken: r.actionTaken, notTaken: r.notTaken };
      const bc = r.byCourier[courier] ?? { total: 0, actionTaken: 0, notTaken: 0 };
      return { label: r.label, ndrCode: r.ndrCode, total: bc.total, actionTaken: bc.actionTaken, notTaken: bc.notTaken };
    }).filter((r) => r.total > 0).sort((a, b) => b.total - a.total);
  }, [ndr, courier]);
  const totals = rows.reduce((a, r) => ({ total: a.total + r.total, actionTaken: a.actionTaken + r.actionTaken, notTaken: a.notTaken + r.notTaken }), { total: 0, actionTaken: 0, notTaken: 0 });
  const chart = rows.map((r) => ({ name: r.label, taken: r.actionTaken, pending: r.notTaken }));
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-slate-800">NDR Overview — reasons & action status</h3>
        <div>
          <label className="text-xs text-slate-500 mr-2">Courier</label>
          <select value={courier} onChange={(e) => setCourier(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
            <option value="">All couriers</option>
            {ndr.couriers.map((c) => <option key={c.courierId} value={String(c.courierId)}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <MiniStat label="Total NDR" value={num(totals.total)} tone="text-slate-900" />
        <MiniStat label="Action Taken" value={`${num(totals.actionTaken)} · ${share(totals.actionTaken, totals.total)}`} tone="text-emerald-700" />
        <MiniStat label="Action Not Taken" value={`${num(totals.notTaken)} · ${share(totals.notTaken, totals.total)}`} tone="text-rose-700" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">NDR Reason</th>
                <th className="text-right px-4 py-2.5">Total</th>
                <th className="text-right px-4 py-2.5">Share</th>
                <th className="text-right px-4 py-2.5">Action Taken</th>
                <th className="text-right px-4 py-2.5">Not Taken</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400">No NDR for these filters.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.label}
                  onClick={() => onDrill({ kind: 'ndr', ndrCode: r.ndrCode, courierId: courier ? Number(courier) : undefined, label: r.label })}
                  className="border-t border-slate-100 cursor-pointer hover:bg-emerald-50/60">
                  <td className="px-4 py-2 text-slate-700">
                    <span className="text-emerald-700 hover:underline inline-flex items-center gap-1">{r.label}<ExternalLink size={11} className="opacity-50" /></span>
                  </td>
                  <td className="px-4 py-2 text-right font-medium tabular-nums">{num(r.total)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-500">{share(r.total, totals.total)}</td>
                  {/* Action Taken / Not Taken are shares of THIS REASON's own total, not of all NDRs
                      — "68% of Customer Not Available has been actioned" is the question being asked
                      here, and dividing by the grand total would answer a different one. */}
                  <td className="px-4 py-2 text-right text-emerald-700 tabular-nums">
                    {num(r.actionTaken)} <span className="text-[11px] text-emerald-600/70">({share(r.actionTaken, r.total)})</span>
                  </td>
                  <td className="px-4 py-2 text-right text-rose-700 tabular-nums">
                    {num(r.notTaken)} <span className="text-[11px] text-rose-600/70">({share(r.notTaken, r.total)})</span>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr className="border-t-2 bg-slate-50 font-semibold">
                <td className="px-4 py-2">Total</td>
                <td className="px-4 py-2 text-right tabular-nums">{num(totals.total)}</td>
                <td className="px-4 py-2 text-right tabular-nums">100%</td>
                <td className="px-4 py-2 text-right tabular-nums">{num(totals.actionTaken)} <span className="text-[11px] font-normal">({share(totals.actionTaken, totals.total)})</span></td>
                <td className="px-4 py-2 text-right tabular-nums">{num(totals.notTaken)} <span className="text-[11px] font-normal">({share(totals.notTaken, totals.total)})</span></td>
              </tr></tfoot>
            )}
          </table>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          {chart.length === 0 ? (
            <div className="h-[320px] flex items-center justify-center text-slate-400 text-sm">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(320, chart.length * 28)}>
              <BarChart data={chart} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v: any) => num(Number(v))} />
                <Legend />
                <Bar dataKey="taken" name="Action Taken" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="pending" name="Not Taken" stackId="a" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, metric, onMetric, children }: { title: string; metric: Metric; onMetric: (m: Metric) => void; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <MetricToggle value={metric} onChange={onMetric} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

// ── Drill-down modal: lists the orders behind the clicked card / row ──────────
type DrillRow = {
  id: number; orderNo: string; awbNo: string | null; lrNo: string | null;
  statusId: number; statusLabel: string; deliveryType: string; bookingDate: string;
  invoiceValue: number; codValue: number; customerName: string; customerMobile: string;
  customerCity: string; zoneLabel: string; courierName: string;
};
type DrillResp = { title: string; orderType: 'B2C' | 'B2B'; total: number; page: number; pageSize: number; totalPages: number; rows: DrillRow[] };

const PAGE_SIZE = 20;
function drillParams(d: Drill) {
  return {
    kind: d.kind,
    ...(d.metric ? { metric: d.metric } : {}),
    ...(d.statusId != null ? { statusId: d.statusId } : {}),
    ...(d.zoneCode != null ? { zoneCode: d.zoneCode } : {}),
    ...(d.courierId != null ? { courierId: d.courierId } : {}),
    ...(d.ndrCode != null ? { ndrCode: d.ndrCode } : {}),
    ...(d.deliveryType ? { deliveryType: d.deliveryType } : {}),
  };
}
const fmtDate = (s: string) => { const d = new Date(s); return isNaN(+d) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); };

function DrillModal({ drill, onClose, orderType, from, to }: { drill: Drill | null; onClose: () => void; orderType: 'B2C' | 'B2B'; from: string; to: string }) {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [drill]);

  const { data, isFetching } = useQuery<DrillResp>({
    queryKey: ['merchant-drill', orderType, from, to, drill, page],
    enabled: !!drill,
    queryFn: async () => (await api.get<DrillResp>('/dashboard/merchant/drill', {
      params: { orderType, from, to, page, pageSize: PAGE_SIZE, ...drillParams(drill!) },
    })).data,
  });

  if (!drill) return null;
  const refLabel = orderType === 'B2B' ? 'LR No.' : 'AWB No.';
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const openOrder = (id: number) => { onClose(); nav(`/orders-${orderType.toLowerCase()}/${id}`); };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 sm:p-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl my-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-sm font-semibold text-slate-800">{data?.title ?? drill.label}</div>
            <div className="text-xs text-slate-400">{orderType} · {from} → {to}{isFetching ? ' · loading…' : total ? ` · ${num(total)} orders` : ''}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={18} /></button>
        </div>
        {/* Body */}
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2.5">Order No.</th>
                <th className="text-left px-4 py-2.5">{refLabel}</th>
                <th className="text-left px-4 py-2.5">Customer</th>
                <th className="text-left px-4 py-2.5">City</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Booked</th>
                <th className="text-right px-4 py-2.5">Value</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && rows.length === 0 ? (
                <TableSkeleton rows={6} cols={8} />
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-slate-400">No orders found.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} onClick={() => openOrder(r.id)} className="border-t border-slate-100 cursor-pointer hover:bg-emerald-50/60">
                  <td className="px-4 py-2"><span className="text-primary hover:underline font-medium">{r.orderNo}</span></td>
                  <td className="px-4 py-2 text-slate-600 tabular-nums">{(orderType === 'B2B' ? r.lrNo : r.awbNo) || '—'}</td>
                  <td className="px-4 py-2 text-slate-700">
                    <div className="truncate max-w-[180px]">{r.customerName}</div>
                    <div className="text-xs text-slate-400">{r.customerMobile}</div>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{r.customerCity}</td>
                  <td className="px-4 py-2"><span className="text-xs bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">{r.statusLabel}</span></td>
                  <td className="px-4 py-2 text-slate-600">{r.deliveryType === 'COD' ? 'COD' : 'Prepaid'}</td>
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{fmtDate(r.bookingDate)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">₹{money(r.invoiceValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Footer / pagination */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 text-sm">
          <div className="text-slate-500">{total > 0 ? `Page ${page} of ${totalPages} · ${num(total)} orders` : ' '}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || isFetching}
              className="px-2 py-1.5 border rounded text-slate-600 disabled:opacity-40 hover:bg-slate-50 flex items-center gap-1"><ChevronLeft size={14} /> Prev</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || isFetching}
              className="px-2 py-1.5 border rounded text-slate-600 disabled:opacity-40 hover:bg-slate-50 flex items-center gap-1">Next <ChevronRight size={14} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
